import {
  type FocusEvent,
  forwardRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import cancelEvent from '../utils/cancelEvent'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'

export interface InlineEditRef {
  edit: () => void
}

export const InlineEditStrategy = {
  manual: 'manual',
  click: 'click'
} as const

type InlineEditStrategy = (typeof InlineEditStrategy)[keyof typeof InlineEditStrategy]

type Props = {
  value: string
  onChange: (value: string) => void
  editStrategy?: InlineEditStrategy
  contentWrapperProps?: HTMLAttributes<HTMLDivElement>
  formProps?: HTMLAttributes<HTMLFormElement>
  inputProps?: InputHTMLAttributes<HTMLInputElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>

const InlineEdit = forwardRef<InlineEditRef, PropsWithChildren<Props>>(
  (
    {
      value,
      formProps,
      inputProps,
      children,
      onChange,
      editStrategy = InlineEditStrategy.click,
      contentWrapperProps,
      ...props
    },
    ref
  ) => {
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const formRef = useRef<HTMLFormElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const cancel = useRef<boolean>(false)

    const revertChange = useCallback(() => {
      cancel.current = true

      setIsEditing(false)
    }, [setIsEditing])

    const commitChange = useCallback(() => {
      if (!formRef.current || !inputRef.current) return

      const newValue = inputRef.current.value.trim()

      if (value === newValue) {
        setIsEditing(false)

        return
      }

      const isValid = formRef.current.checkValidity()

      if (!isValid) {
        formRef.current.reportValidity()

        return
      }

      onChange(newValue)
      setIsEditing(false)
    }, [inputRef.current, onChange, revertChange, setIsEditing])

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'Enter') {
          cancelEvent(event)
          commitChange()
        } else if (event.code === 'Escape') {
          cancelEvent(event)
          revertChange()
        }

        inputProps?.onKeyDown?.(event)
      },
      [commitChange, inputProps?.onKeyDown, revertChange]
    )

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLInputElement, Element>) => {
        if (cancel.current) {
          revertChange()
        } else {
          commitChange()
        }

        inputProps?.onBlur?.(event)
      },
      [cancel.current, commitChange, inputProps?.onBlur]
    )

    const triggerEditFromClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        cancelEvent(event)
        setIsEditing(true)
        contentWrapperProps?.onClick?.(event)
      },
      [contentWrapperProps?.onClick, setIsEditing]
    )

    const triggerEditFromKeyboardDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          cancelEvent(event)
          setIsEditing(true)
        }

        contentWrapperProps?.onKeyDown?.(event)
      },
      [contentWrapperProps?.onKeyDown, setIsEditing]
    )

    useImperativeHandle(
      ref,
      () => ({
        edit: () => {
          setIsEditing(true)

          setTimeout(() => {
            inputRef.current?.focus()
          }, 100)
        }
      }),
      [setIsEditing]
    )

    if (!isEditing) {
      if (editStrategy === InlineEditStrategy.click) {
        return (
          <div {...props}>
            <div
              tabIndex={0}
              {...contentWrapperProps}
              className={resolveTailwindClasses(
                'rounded-lg px-3 py-1.5 outline-none outline-offset-2 outline-transparent hover:bg-white/5 focus-visible:outline-1 focus-visible:outline-white',
                contentWrapperProps?.className
              )}
              onClick={triggerEditFromClick}
              onKeyDown={triggerEditFromKeyboardDown}
            >
              {children}
            </div>
          </div>
        )
      } else {
        return children
      }
    }

    return (
      <div {...props} className={resolveTailwindClasses('flex items-stretch', props.className)}>
        <form ref={formRef} {...formProps} className={resolveTailwindClasses('w-full', formProps?.className)}>
          <input
            ref={inputRef}
            defaultValue={value}
            autoFocus
            name="inlineEditINput"
            minLength={3}
            maxLength={30}
            {...inputProps}
            className={resolveTailwindClasses(
              'w-full rounded-lg bg-primary-200 px-3 py-1.5 text-primary-800 outline-none outline-offset-2 outline-transparent placeholder:text-primary-800/60 focus-visible:outline-1 focus-visible:outline-white',
              inputProps?.className
            )}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={cancelEvent}
          />
        </form>
      </div>
    )
  }
)

InlineEdit.displayName = 'InlineEdit'

export { InlineEdit }
